<template>
  <sticky-banner
    v-if="isBannerVisible"
    :banner="banner" @clicked="handleClick" />
</template>

<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { IconName } from '@ankorstore/design-system';
import { StickyBanner } from '@client/modules/shared/components';
import {
  updateBrandReactivationStatus,
  getBrandReactivationStatus,
} from '@client/monolith/legacy/services/api/brand-reactivation';
import { BrandReactivationRequest, BrandReactivationStatusLabel } from '@client/monolith/legacy/types/api/brand';
import { captureRetailerCoreDiscoveryException } from '@core/plugins/sentry/helper';

const { t } = useI18n();
const brand = ref(null);
const status = ref(null);

const banner = computed(() => {
  return {
    title: titleByStatus.value,
    description: descriptionByStatus.value,
    buttonText: buttonTextByStatus.value,
    icon: iconByStatus.value,
    color: colorByStatus.value,
    status: status.value,
  };
});
const titleByStatus = computed(() => {
  switch (status.value) {
    case BrandReactivationStatusLabel.not_requested:
      return t('discovery.brandReactivationBanner.title');
    case BrandReactivationStatusLabel.pending:
      return t('discovery.brandReactivationBanner.pending.title');
    default:
      return t('discovery.brandReactivationBanner.reactivated.title');
  }
});
const descriptionByStatus = computed(() => {
  switch (status.value) {
    case BrandReactivationStatusLabel.not_requested:
      return t('discovery.brandReactivationBanner.description');
    case BrandReactivationStatusLabel.pending:
      return null;
    default:
      return null;
  }
});
const buttonTextByStatus = computed(() => {
  switch (status.value) {
    case BrandReactivationStatusLabel.not_requested:
      return t('discovery.brandReactivationBanner.buttonText');
    case BrandReactivationStatusLabel.pending:
      return null;
    default:
      return t('discovery.brandReactivationBanner.reactivated.buttonText');
  }
});
const iconByStatus = computed((): IconName => {
  switch (status.value) {
    case BrandReactivationStatusLabel.not_requested:
      return 'exclamation-triangle';
    case BrandReactivationStatusLabel.pending:
      return 'info-circle';
    default:
      return 'patch-check';
  }
});
const colorByStatus = computed(() => {
  switch (status.value) {
    case BrandReactivationStatusLabel.not_requested:
      return 'error';
    case BrandReactivationStatusLabel.pending:
      return 'info';
    default:
      return 'success';
  }
});

const isBannerVisible = computed(() => {
  if (!brand.value?.data?.meta?.brandStatus || !brand.value?.data?.attributes?.status) {
    return false;
  }
  return brand.value.data.meta.brandStatus === 'offline' 
    || (brand.value.data.meta.brandStatus === 'online' 
    && status.value === BrandReactivationStatusLabel.approved);
});

const handleClick = async () => {
  try {
    const response = await updateBrandReactivationStatus(
      status.value === BrandReactivationStatusLabel.approved
        ? BrandReactivationRequest.dismissBanner
        : BrandReactivationRequest.requestReactivation
    );
    status.value = response.data.attributes.status;
  } catch (error) {
    captureRetailerCoreDiscoveryException(error);
  }
};
onBeforeMount(async () => {
  try {
    brand.value = await getBrandReactivationStatus();
    status.value = brand.value?.data?.attributes?.status;
  } catch (error) {
    captureRetailerCoreDiscoveryException(error);
  }
});
</script>
